/**
 * NewTransformationWizard — Multi-step wizard for creating a new transformation
 * with mixed sources (files + connectors), column selection, and data preview.
 *
 * Step 1: Add sources (files + connectors)
 * Step 2: Configure columns and preview data
 * Step 3: Review and submit
 *
 * Drop-in replacement for UploadDialog — same props interface.
 */
import React, { useCallback, useState } from 'react';
import { validateCsvFiles } from '../../utils/csvValidation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  CloudUpload,
} from '@mui/icons-material';
import { apiService, Schema } from '../../services/api';
import { addConnectorSource } from '../../services/projectSourceApi';
import { createBatchFromConnector } from '../../services/batchApi';
import type { ConnectorSourceEntry } from './ConnectorSourcePicker';
import WizardStepSources from './WizardStepSources';
import WizardStepConfigure from './WizardStepConfigure';
import WizardStepReview from './WizardStepReview';
import SideDrawer from '../common/SideDrawer';

const STEPS = ['Add Sources', 'Configure', 'Review & Submit'];

interface NewTransformationWizardProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete: (batchInfo: {
    batch_id: string;
    status: string;
    files?: string[];
    filename?: string;
  }) => void;
  projectId: string | null;
  schemaSet: Schema | null | undefined;
  title?: string;
  defaultSourceConnectorId?: string | null;
}

const normaliseError = (error: unknown): string => {
  const fallback = 'Submission failed. Please try again.';
  if (!error) return fallback;
  const respDetail = (error as any)?.response?.data?.detail;
  if (typeof respDetail === 'string' && respDetail.trim()) return respDetail;
  const respMessage = (error as any)?.response?.data?.message;
  if (typeof respMessage === 'string' && respMessage.trim()) return respMessage;
  const msg = (error as any)?.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
};

export { isDangerousCsvCell } from '../../utils/csvValidation';

const NewTransformationWizard: React.FC<NewTransformationWizardProps> = ({
  open,
  onClose,
  onUploadComplete,
  projectId,
  schemaSet,
  title = 'New Transformation',
  defaultSourceConnectorId,
}) => {
  const [step, setStep] = useState(0);
  const [transformationName, setTransformationName] = useState('');

  const [files, setFiles] = useState<File[]>([]);
  const [connectorSources, setConnectorSources] = useState<ConnectorSourceEntry[]>([]);
  const [columnSelections, setColumnSelections] = useState<Map<string, string[]>>(new Map());
  const [configureStepValid, setConfigureStepValid] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  const totalSources = files.length + connectorSources.length;

  const resetState = useCallback(() => {
    setStep(0);
    setTransformationName('');
    setFiles([]);
    setConnectorSources([]);
    setColumnSelections(new Map());
    setConfigureStepValid(false);
    setSubmitting(false);
    setSubmitError(null);
    setUploadPercent(null);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetState();
    onClose();
  }, [submitting, resetState, onClose]);


  const handleSubmit = useCallback(async () => {
    if (!projectId || totalSources === 0) return;
    setSubmitting(true);
    setSubmitError(null);

  

    const validationError = await validateCsvFiles(files);
    if (validationError) {
      setSubmitError(validationError);
      setSubmitting(false);
      return; 
    }

    const columnsByTable: Record<string, string[]> = {};
    columnSelections.forEach((cols, key) => {
      let tableName = key;
      if (key.startsWith('file::')) {
        const parts = key.split('::');
        const fileName = parts[1] || key;
        tableName = fileName.replace(/\.[^.]+$/, '');
        if (parts.length > 3) {
          // Include the sheet name in the expected backend format
          tableName = `${tableName}__${parts[3]}`;
        }
      } else if (key.includes('::')) {
        const parts = key.split('::');
        tableName = parts[parts.length - 1] || key;
      }
      columnsByTable[tableName] = cols;
    });
    const hasColumnSelections = Object.keys(columnsByTable).length > 0;

    try {
      let batchId: string | undefined;

      // Exclude files where every sheet (or the file itself) was explicitly deselected.
      const filesToUpload = files.filter((file) => {
        const baseKey = `file::${file.name}::${file.size}`;
        const prefix = `${baseKey}::`;
        const sheetKeys = Array.from(columnSelections.keys()).filter((k) => k.startsWith(prefix));
        if (sheetKeys.length > 0) {
          return sheetKeys.some((sk) => (columnSelections.get(sk) ?? []).length > 0);
        }
        const cols = columnSelections.get(baseKey);
        return !cols || cols.length > 0;
      });

      if (filesToUpload.length === 0 && connectorSources.length === 0) {
        setSubmitError('Please select at least one source with columns to continue.');
        setSubmitting(false);
        return;
      }

      if (filesToUpload.length > 0) {
        setUploadPercent(0);
        const uploadResp = await apiService.uploadBatch(filesToUpload, {
          projectId,
          schemaId: schemaSet?.schema_id ?? null,
          columnsByTable: hasColumnSelections ? columnsByTable : undefined,
          batchName: transformationName.trim() || undefined,
          onUploadProgress: (p) => setUploadPercent(p.percent),
        });
        setUploadPercent(null);
        batchId = uploadResp.batch_id;
      }

      const groups = Object.entries(
        connectorSources.reduce<Record<string, ConnectorSourceEntry[]>>((acc, entry) => {
          (acc[entry.connectorId] ??= []).push(entry);
          return acc;
        }, {}),
      );

      for (let i = 0; i < groups.length; i++) {
        const [connectorId, entries] = groups[i];
        const tables = entries.map((e) => e.tableName);

        if (!batchId && i === 0) {
          const result = await createBatchFromConnector({
            projectId,
            connectorId,
            targetSchemaId: schemaSet?.schema_id ?? undefined,
            streams: tables,
            columnsByTable: hasColumnSelections ? columnsByTable : undefined,
            batchName: transformationName.trim() || undefined,
          });
          batchId = result.batch_id;
        } else {
          try {
            const result = await addConnectorSource(projectId, {
              connector_id: connectorId,
              tables,
              batch_id: batchId,
              columns_by_table: hasColumnSelections ? columnsByTable : undefined,
            });
            batchId = batchId ?? (result as any).batch_id;
          } catch (err: any) {
            if (err?.response?.status === 409) {
              batchId = batchId ?? err?.response?.data?.batch_id;
            } else {
              throw err;
            }
          }
        }
      }

      if (batchId) {
        resetState();
        onClose();
        onUploadComplete({
          batch_id: batchId,
          status: 'pending',
          files: filesToUpload.length > 0 ? filesToUpload.map((f) => f.name) : undefined,
          filename: filesToUpload.length > 0 ? filesToUpload[0].name : undefined,
        });
      }
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmitError(normaliseError(error));
    } finally {
      setSubmitting(false);
    }
  }, [projectId, files, connectorSources, columnSelections, totalSources, resetState, onClose, onUploadComplete, validateCsvFiles,]);

  const canProceedFromStep0 = totalSources > 0;
  const canProceedFromStep1 = configureStepValid;
  const canSubmit = totalSources > 0 && !submitting;

  const drawerTitle = (
    <Stack direction="row" alignItems="baseline" spacing={1.5}>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Step {step + 1} of {STEPS.length}
      </Typography>
    </Stack>
  );

  const footer = (
    <>
      <Button onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Box sx={{ flex: 1 }} />

      {step > 0 && (
        <Button
          startIcon={<BackIcon />}
          onClick={() => setStep((s) => s - 1)}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
      )}

      {step < 2 && (
        <Tooltip
          title={step === 1 && !canProceedFromStep1 ? 'Please select at least one column from one source to continue.' : ''}
        >
          <span>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 ? !canProceedFromStep0 : !canProceedFromStep1}
              sx={{ textTransform: 'none' }}
            >
              Next
            </Button>
          </span>
        </Tooltip>
      )}

      {step === 2 && (
        <Stack direction="row" alignItems="center" spacing={2}>
          {submitting && uploadPercent !== null && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 180 }}>
              <LinearProgress
                variant="determinate"
                value={uploadPercent}
                sx={{ flex: 1, minWidth: 120, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {uploadPercent}%
              </Typography>
            </Stack>
          )}
          {submitting && uploadPercent === null && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 180 }}>
              <LinearProgress
                variant="indeterminate"
                sx={{ flex: 1, minWidth: 120, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Processing...
              </Typography>
            </Stack>
          )}
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <CloudUpload />}
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ textTransform: 'none' }}
          >
            {submitting
              ? uploadPercent !== null
                ? 'Uploading...'
                : 'Processing...'
              : 'Start Transformation'}
          </Button>
        </Stack>
      )}
    </>
  );

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title={drawerTitle}
      footer={footer}
      disableClose={submitting}
      defaultWidth="min(72vw, 960px)"
    >
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>
                <Typography variant="caption" sx={{ fontSize: 11 }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {step === 0 && (
          <Stack spacing={2}>
            <TextField
              label="Transformation Name"
              placeholder="e.g. Q4 Sales Import"
              value={transformationName}
              onChange={(e) => {
                if (e.target.value.length <= 200) setTransformationName(e.target.value);
              }}
              size="small"
              fullWidth
              helperText={transformationName.length > 0 ? `${transformationName.length}/200 — Optional` : 'Optional — defaults to the first file name'}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 200 }}
            />
            <WizardStepSources
              projectId={projectId}
              files={files}
              connectorSources={connectorSources}
              onFilesChange={setFiles}
              onConnectorSourcesChange={setConnectorSources}
            />
          </Stack>
        )}

        {step === 1 && (
          <WizardStepConfigure
            files={files}
            connectorSources={connectorSources}
            columnSelections={columnSelections}
            onColumnSelectionsChange={setColumnSelections}
            onValidityChange={setConfigureStepValid}
          />
        )}

        {step === 2 && (
          <WizardStepReview
            files={files}
            connectorSources={connectorSources}
            columnSelections={columnSelections}
          />
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </Box>
    </SideDrawer>
  );
};

export default NewTransformationWizard;
